import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, FileText, CheckCircle2, XCircle, AlertTriangle, PenTool, Type, RotateCcw } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { useInAppDialog } from './InAppDialog';

interface FerpaConsentModalProps {
  onGrant: (signatureData: { type: 'draw' | 'type', value: string, name: string, date: string }) => void;
  onDecline: () => void;
}

export const FerpaConsentModal: React.FC<FerpaConsentModalProps> = ({ onGrant, onDecline }) => {
  const { showAlert } = useInAppDialog();
  const [signatureType, setSignatureType] = useState<'draw' | 'type'>('type');
  const [typedName, setTypedName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isAttested, setIsAttested] = useState(false);
  const sigPad = useRef<SignatureCanvas>(null);

  const clearSignature = () => {
    sigPad.current?.clear();
    setIsDrawing(false);
  };

  const handleGrant = () => {
    if (!isAttested) {
      showAlert({
        title: "Attestation Required",
        message: "You must check the affirmative statutory certification box to confirm your legal authority under FERPA before submitting.",
        type: "warning"
      });
      return;
    }

    const date = new Date().toISOString();
    if (signatureType === 'type') {
      if (!typedName.trim()) {
        showAlert({
          title: "Name Required",
          message: "Please type your full legal name to sign.",
          type: "warning"
        });
        return;
      }
      onGrant({ type: 'type', value: typedName, name: typedName, date });
    } else {
      if (sigPad.current?.isEmpty()) {
        showAlert({
          title: "Signature Required",
          message: "Please provide a signature on the pad.",
          type: "warning"
        });
        return;
      }
      const signatureValue = sigPad.current?.getCanvas().toDataURL('image/png') || '';
      onGrant({ type: 'draw', value: signatureValue, name: typedName || 'Digital Signature', date });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="bg-[#0094c8] p-6 text-white shrink-0">
          <div className="flex items-center space-x-3 mb-2">
            <Shield className="w-6 h-6 text-yellow-300" />
            <h2 className="text-xl font-black uppercase tracking-tight">WBL Portal: FERPA Consent</h2>
          </div>
          <p className="text-white/80 text-xs font-bold uppercase tracking-widest">
            Unified Authorization for Disclosure of Education Records
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6 text-slate-700 leading-relaxed scrollbar-thin scrollbar-thumb-slate-200">
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-800 font-medium">
              <span className="font-black uppercase tracking-wider block mb-1">Notice:</span>
              This document serves as the formal written consent required under the Family Educational Rights and Privacy Act (FERPA) (20 U.S.C. § 1232g; 34 CFR Part 99). 
              <br /><br />
              <span className="font-bold underline italic">For Students Under 18:</span> This form must be signed by a Parent or Legal Guardian.
              <br />
              <span className="font-bold underline italic">For Students 18 or Older:</span> This form must be signed by the Student (the "Eligible Student").
            </div>
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-black text-[#0094c8] uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 bg-[#0094c8]/10 rounded-full flex items-center justify-center text-[10px]">1</span>
              Identification of Parties
            </h3>
            <p className="text-xs pl-7">
              I hereby authorize the <span className="font-bold">[Student’s School District]</span> and the WBL Portal to disclose the protected education records described below to the following "Authorized Recipients":
            </p>
            <ul className="list-disc pl-12 text-xs space-y-1.5 font-medium">
              <li><span className="text-slate-900 font-bold">Regional Workforce Administrators:</span> The Sponsoring Chamber of Commerce (OneSpartanburg, Inc).</li>
              <li><span className="text-slate-900 font-bold">Educational Oversight:</span> Verified School and Career Advisors and Facilitators.</li>
              <li><span className="text-slate-900 font-bold">Prospective Professional Partners:</span> Employers and Internship Providers approved by the Regional Chamber of Commerce and explicitly selected by the student.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-black text-[#0094c8] uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 bg-[#0094c8]/10 rounded-full flex items-center justify-center text-[10px]">2</span>
              Specific Records to be Disclosed
            </h3>
            <p className="text-xs pl-7">
              The scope of this authorization includes the following "Personally Identifiable Information" (PII):
            </p>
            <ul className="list-disc pl-12 text-xs space-y-1.5 font-medium">
              <li><span className="text-slate-900 font-bold">Academic Credentials:</span> Full name, date of birth, email address, phone number, school and district affiliation, enrollment status, and graduation year.</li>
              <li><span className="text-slate-900 font-bold">Professional Identity:</span> Resume, career cluster and subcluster interests, skills, and professional summaries.</li>
              <li><span className="text-slate-900 font-bold">Workplace Performance:</span> Application and placement status, and numerical/qualitative evaluations (including feedback finalized by supervisors).</li>
              <li><span className="text-slate-900 font-bold">Communication Logs:</span> Interview status related to work-based learning placements.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-black text-[#0094c8] uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 bg-[#0094c8]/10 rounded-full flex items-center justify-center text-[10px]">3</span>
              Purpose of Disclosure
            </h3>
            <p className="text-xs pl-7">
              This disclosure is made for the following legitimate educational and professional purposes:
            </p>
            <ul className="list-disc pl-12 text-xs space-y-1.5 font-medium">
              <li><span className="text-slate-900 font-bold">Work-Based Learning (WBL) Administration:</span> To facilitate applications, interviews, and regional placements.</li>
              <li><span className="text-slate-900 font-bold">Academic Assessment:</span> To allow the School District to monitor progress and award academic credit for off-site experiences and determine College and Career Readiness status.</li>
              <li><span className="text-slate-900 font-bold">Program Auditing:</span> To provide the Chamber of Commerce with data necessary for workforce development reporting and funding compliance.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-black text-[#0094c8] uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 bg-[#0094c8]/10 rounded-full flex items-center justify-center text-[10px]">4</span>
              SC HB 3431 Compliance
            </h3>
            <p className="text-xs pl-7">
              In accordance with the South Carolina Age-Appropriate Design Code (2026), Groundwork provides:
            </p>
            <ul className="list-disc pl-12 text-xs space-y-1.5 font-medium">
              <li><span className="text-slate-900 font-bold">High-Privacy Defaults:</span> This account is set to maximum privacy by default.</li>
              <li><span className="text-slate-900 font-bold">Transparency:</span> Students will receive "Obvious Notice" whenever a Parent or Advisor has provided consent or verified enrollment.</li>
              <li><span className="text-slate-900 font-bold">Data Minimization:</span> WBL Portal will not use this data for secondary marketing or to train third-party AI models.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-black text-[#0094c8] uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 bg-[#0094c8]/10 rounded-full flex items-center justify-center text-[10px]">5</span>
              Right to Revoke & Expiration
            </h3>
            <ul className="list-disc pl-12 text-xs space-y-1.5 font-medium">
              <li><span className="text-slate-900 font-bold">Revocation:</span> I understand that I may revoke this consent at any time via the Groundwork "Legal Authorizations" dashboard. Revocation is effective immediately upon toggling the "Off" status, though it cannot retrieve data previously shared.</li>
              <li><span className="text-slate-900 font-bold">Expiration:</span> This authorization expires automatically upon the student's graduation or the termination of the regional contract with the Sponsoring Chamber, whichever occurs first.</li>
            </ul>
          </section>

          {/* Signature Section */}
          <div className="pt-6 border-t border-slate-100 space-y-5">
            {/* Mandatory Statutory Attestation Representation */}
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-2xl space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 bg-amber-200/80 px-2 py-0.5 rounded-md">
                  Statutory Legal Representation
                </span>
                <span className="text-[10px] font-mono font-bold text-amber-800">
                  FERPA 34 CFR § 99.30
                </span>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={isAttested}
                  onChange={(e) => setIsAttested(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-amber-400 text-[#0094c8] focus:ring-[#0094c8] accent-[#0094c8] shrink-0 cursor-pointer"
                />
                <span className="text-xs font-semibold text-amber-950 leading-relaxed select-none">
                  "I certify under penalty of perjury and law that I am the legal parent or court-appointed legal guardian of the minor student named herein (or the eligible student aged 18+) and possess full legal authority to grant educational consent under FERPA (34 CFR § 99.30)."
                </span>
              </label>
            </div>

            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Authorization Signature</span>
                  <p className="text-[11px] text-slate-600 font-medium">
                    Select your signature method and sign below.
                  </p>
                </div>
                <div className="flex bg-white rounded-lg border border-slate-200 p-1">
                  <button 
                    onClick={() => setSignatureType('type')}
                    className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${signatureType === 'type' ? 'bg-[#0094c8] text-white' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <Type className="w-3 h-3" />
                    Type
                  </button>
                  <button 
                    onClick={() => setSignatureType('draw')}
                    className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${signatureType === 'draw' ? 'bg-[#0094c8] text-white' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <PenTool className="w-3 h-3" />
                    Draw
                  </button>
                </div>
              </div>

              {signatureType === 'type' ? (
                <div className="space-y-3">
                  <input 
                    type="text"
                    placeholder="Type your full legal name"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:ring-2 focus:ring-[#0094c8] outline-none transition-all placeholder:text-slate-300"
                  />
                  <p className="text-[10px] text-slate-400 font-mono italic text-center">
                    Digitally Signed as: <span className="text-slate-600 font-bold underline">{typedName || '[Your Name]'}</span>
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative h-32 bg-white border border-slate-200 rounded-xl overflow-hidden group">
                    <SignatureCanvas 
                      ref={sigPad}
                      penColor="black"
                      canvasProps={{ className: 'w-full h-full cursor-crosshair' }}
                      onBegin={() => setIsDrawing(true)}
                    />
                    {!isDrawing && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">Sign Here</p>
                      </div>
                    )}
                    <button 
                      onClick={clearSignature}
                      className="absolute bottom-2 right-2 p-1.5 bg-slate-100 text-slate-400 rounded-md hover:bg-slate-200 hover:text-slate-600 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <input 
                    type="text"
                    placeholder="Verify Printed Name"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-[11px] font-bold text-slate-900 focus:ring-2 focus:ring-[#0094c8] outline-none transition-all placeholder:text-slate-300"
                  />
                </div>
              )}

              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                By signing, I certify that I am authorized to grant this release and my signature is legally binding.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-slate-50 border-t border-slate-200 flex flex-col md:flex-row items-center justify-center gap-4 shrink-0">
          <button
            onClick={onDecline}
            className="w-full md:w-auto px-8 py-3 rounded-xl border border-slate-200 text-slate-500 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-red-500 hover:border-red-500/20 transition-all flex items-center justify-center gap-2"
          >
            <XCircle className="w-4 h-4" />
            I Decline
          </button>
          <button
            onClick={handleGrant}
            className="w-full md:w-auto px-10 py-3 rounded-xl bg-[#0094c8] text-white font-black uppercase tracking-widest text-xs hover:bg-[#007ba6] shadow-lg shadow-[#0094c8]/20 transition-all flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            I Grant Full Consent
          </button>
        </div>
      </motion.div>
    </div>
  );
};
